import React, { useState, useEffect, useRef } from "react";
import { Modal, Select, Button, Spin, Input, message } from "antd";
import { useQuery, gql, useMutation } from "@apollo/client";
import { useLocation } from "react-router-dom";
import "../../src/tailwind/tailwind.css";
import { Get_Account_Experts, Get_City, Get_Expert_Video, Get_States, Get_Video_Activity_By_Role, Get_Video_Task_By_Activity, Get_Video_Subtask_By_Task, GET_VIDEOS_WITH_FILTERS, CREATE_NEW_WEB_NOTIFICATION } from "../services/userAPI";
import { SearchOutlined } from "@ant-design/icons";
import TranslateIcon from '@mui/icons-material/Translate';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import "./pushVideosFromAdminPanel.css";

const PushVideosFromAdminPanel = ({ visible, onCancel, userData }) => {
  const [step, setStep] = useState(1);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedSubtask, setSelectedSubtask] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [selectedStar, setSelectedStar] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedCreatedAt, setSelectedCreatedAt] = useState(null);
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [nextPage, setNextPage] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [noMoreVideos, setNoMoreVideos] = useState(false);
  const [currentFilter, setCurrentFilter] = useState({});
  const [loadingMore, setLoadingMore] = useState(false);
  const [applying, setApplying] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [activeTooltipIndex, setActiveTooltipIndex] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const titleRefs = useRef([]);
  const videoGridRef = useRef(null);
  const notificationSectionRef = useRef(null); // Ref for the notification section

  const location = useLocation();
  const roleId = location.state?.roleId || 10;
  const accountId = location.state?.accountId || 1;

  // Update titleRefs when videos change
  useEffect(() => {
    titleRefs.current = videos.map(() => React.createRef());
  }, [videos]);

  // Scroll to notification section when step changes to 2
  useEffect(() => {
    if (step === 2 && notificationSectionRef.current) {
      notificationSectionRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [step]);

  // Fetch Activities
  const { data: activitiesData, loading: activitiesLoading } = useQuery(Get_Video_Activity_By_Role, {
    variables: { jobroleId: roleId, searchTerm: "" },
    skip: !roleId,
  });

  // Fetch Tasks
  const { data: tasksData, loading: tasksLoading } = useQuery(Get_Video_Task_By_Activity, {
    skip: !selectedActivity,
    variables: { activityId: selectedActivity, searchTerm: "" },
  });

  // Fetch Subtasks
  const { data: subtasksData, loading: subtasksLoading } = useQuery(Get_Video_Subtask_By_Task, {
    skip: !selectedTask,
    variables: { taskId: selectedTask, searchTerm: "" },
  });

  // Fetch Languages
  const { data: languagesData, loading: languagesLoading } = useQuery(Get_Expert_Video);

  // Fetch Account Experts (Stars)
  const { data: expertsData, loading: expertsLoading } = useQuery(Get_Account_Experts, {
    variables: {
      pagingCriteria: { limit: 100, cursor: null, sortByField: "created_at", sortOrder: "desc" },
      accountId: accountId,
    },
    skip: !accountId,
  });

  // Fetch States
  const { data: statesData, loading: statesLoading } = useQuery(Get_States);

  // Fetch Cities
  const { data: citiesData, loading: citiesLoading } = useQuery(Get_City, {
    variables: { stateId: selectedState || 1 },
    skip: !selectedState,
  });

  // Fetch Videos
  const { data: videosData, loading: videosLoading, refetch: refetchVideos } = useQuery(GET_VIDEOS_WITH_FILTERS, {
    variables: {
      jobroleId: roleId,
      pagingCriteria: { limit: 12, cursor: null, sortByField: "created_at", sortOrder: "desc" },
      forUserId: null,
      searchText: searchText,
      activityId: null,
      taskId: null,
      subtaskId: null,
      creatorId: null,
      languageId: null,
      stateId: null,
      cityId: null,
    },
    skip: !visible,
    onCompleted: (data) => {
      const newVideos = data?.getVideosForJobroleAndWithFilters?.data || [];
      setVideos(newVideos);
      setNextPage(data?.getVideosForJobroleAndWithFilters?.pageMeta?.nextPage);
      setHasNextPage(data?.getVideosForJobroleAndWithFilters?.pageMeta?.hasNextPage);
      setNoMoreVideos(
        !newVideos.length ||
        (!data?.getVideosForJobroleAndWithFilters?.pageMeta?.hasNextPage &&
          !data?.getVideosForJobroleAndWithFilters?.pageMeta?.nextPage)
      );
      setCurrentFilter({
        activityId: null,
        taskId: null,
        subtaskId: null,
        creatorId: null,
        languageId: null,
        stateId: null,
        cityId: null,
        createdAt: null,
      });
    },
  });

  // Mutation for Creating Notification
  const [createNotification] = useMutation(CREATE_NEW_WEB_NOTIFICATION, {
    onCompleted: (data) => {
      setSending(false);
      setStep(1);
      setTitle("");
      setDescription("");
    },
    onError: (error) => {
      console.error("Error creating notification:", error);
      setSending(false);
    },
  });

  // Handle Apply button click
  const handleApplyFilters = () => {
    if (!isFilterApplied()) return;
    setApplying(true);
    const filter = {
      jobroleId: roleId,
      pagingCriteria: {
        limit: 12,
        cursor: null,
        sortByField: "created_at",
        sortOrder: selectedCreatedAt === "newest" ? "desc" : "asc",
      },
      forUserId: null,
      searchText: searchText,
      activityId: selectedActivity,
      taskId: selectedTask,
      subtaskId: selectedSubtask,
      creatorId: selectedStar,
      languageId: selectedLanguage,
      stateId: selectedState,
      cityId: selectedCity,
    };
    setCurrentFilter(filter);
    refetchVideos(filter).then((result) => {
      const newVideos = result.data?.getVideosForJobroleAndWithFilters?.data || [];
      setVideos(newVideos);
      setNextPage(result.data?.getVideosForJobroleAndWithFilters?.pageMeta?.nextPage);
      setHasNextPage(result.data?.getVideosForJobroleAndWithFilters?.pageMeta?.hasNextPage);
      setNoMoreVideos(
        !newVideos.length ||
        (!result.data?.getVideosForJobroleAndWithFilters?.pageMeta?.hasNextPage &&
          !result.data?.getVideosForJobroleAndWithFilters?.pageMeta?.nextPage)
      );
      setApplying(false);
    }).catch(() => setApplying(false));
  };

  // Handle Load More
  const handleLoadMore = () => {
    if (nextPage && hasNextPage) {
      setLoadingMore(true);
      const filter = {
        ...currentFilter,
        pagingCriteria: { ...currentFilter.pagingCriteria, cursor: nextPage },
      };
      refetchVideos(filter).then((result) => {
        const newVideos = result.data?.getVideosForJobroleAndWithFilters?.data || [];
        setVideos((prev) => [...prev, ...newVideos]);
        setNextPage(result.data?.getVideosForJobroleAndWithFilters?.pageMeta?.nextPage);
        setHasNextPage(result.data?.getVideosForJobroleAndWithFilters?.pageMeta?.hasNextPage);
        setNoMoreVideos(
          !newVideos.length ||
          (!result.data?.getVideosForJobroleAndWithFilters?.pageMeta?.hasNextPage &&
            !result.data?.getVideosForJobroleAndWithFilters?.pageMeta?.nextPage)
        );
        setLoadingMore(false);
      }).catch(() => setLoadingMore(false));
    }
  };

  // Check if any filter is applied
  const isFilterApplied = () => {
    return selectedActivity || selectedTask || selectedSubtask || selectedStar || selectedLanguage || selectedState || selectedCity || selectedCreatedAt;
  };

  const activities = activitiesData?.listCriticalActivities || [];
  const tasks = tasksData?.listCriticalTasks || [];
  const subtasks = subtasksData?.listCriticalSubtask || [];
  const languages = languagesData?.mdLanguages || [];
  const experts = expertsData?.getAccountExperts?.data || [];
  const states = statesData?.getStates || [];
  const cities = citiesData?.getCity || [];

  // Handle video selection
  const handleVideoClick = (index) => {
    setSelectedVideos((prev) => (prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]));
  };

  // Handle Next button click
  const handleNext = () => {
    if (selectedVideos.length > 0) {
      setStep(2);
    }
  };

  // Handle Send button click
  const handleSendVideos = async () => {
    if (!title || !description) return;
    setSending(true);

    const notificationData = selectedVideos.map((index) => {
      const video = videos[index];
      return {
        title: title,
        description: description,
        userId: userData?.id,
        notificationType: "PUSH_VIDEO_FOR_USER",
        referenceId: video.id,
        data: null,
      };
    });

    try {
      await createNotification({ variables: { data: notificationData } });
      message.success(`Successfully video sent to ${userData?.name || "Unknown User"}`);
      setSelectedVideos([]);
      setTitle("");
      setDescription("");
      handleCancel();
    } catch (error) {
      console.error("Failed to send notifications:", error);
    } finally {
      setSending(false);
    }
  };

  // Handle modal cancel
  const handleCancel = () => {
    setStep(1);
    setSelectedActivity(null);
    setSelectedTask(null);
    setSelectedSubtask(null);
    setSelectedLanguage(null);
    setSelectedStar(null);
    setSelectedState(null);
    setSelectedCity(null);
    setSelectedCreatedAt(null);
    setSelectedVideos([]);
    setVideos([]);
    setNextPage(null);
    setHasNextPage(false);
    setNoMoreVideos(false);
    setCurrentFilter({});
    setSearchText("");
    setTitle("");
    setDescription("");
    if (onCancel) onCancel();
  };

  // Handle search input change
  // const handleSearchChange = (e) => {
  //   const value = e.target.value;
  //   setSearchText(value);
  //   const filter = {
  //     jobroleId: roleId,
  //     pagingCriteria: { limit: 12, cursor: null, sortByField: "created_at", sortOrder: "desc" },
  //     forUserId: null,
  //     searchText: value,
  //     activityId: selectedActivity,
  //     taskId: selectedTask,
  //     subtaskId: selectedSubtask,
  //     creatorId: selectedStar,
  //     languageId: selectedLanguage,
  //     stateId: selectedState,
  //     cityId: selectedCity,
  //   };
  //   refetchVideos(filter).then((result) => {
  //     const newVideos = result.data?.getVideosForJobroleAndWithFilters?.data || [];
  //     setVideos(newVideos);
  //     setNextPage(result.data?.getVideosForJobroleAndWithFilters?.pageMeta?.nextPage);
  //     setHasNextPage(result.data?.getVideosForJobroleAndWithFilters?.pageMeta?.hasNextPage);
  //     setNoMoreVideos(
  //       !newVideos.length ||
  //       (!result.data?.getVideosForJobroleAndWithFilters?.pageMeta?.hasNextPage &&
  //         !result.data?.getVideosForJobroleAndWithFilters?.pageMeta?.nextPage)
  //     );
  //   }).catch((error) => console.error("Search error:", error));
  // };

  const handleTitleHover = (index, e) => {
    if (titleRefs.current[index]?.current && videos[index]?.title?.length > 60) {
      const rect = titleRefs.current[index].current.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setTooltipPosition({
        top: rect.bottom + scrollTop + 5,
        left: rect.left,
      });
      setActiveTooltipIndex(index);
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={handleCancel}
      footer={null}
      closable={true}
      centered
      className="push-videos-modal"
      width={1200}
      title={<h3 className="font-inter text-xl font-semibold text-center">Push Videos to {userData?.name}</h3>}
    >
      <div className="flex flex-col gap-4 p-4">
        {/* Search Bar */}
        {/* <div className="w-full mb-4">
          <Input
            placeholder="Search video..."
            value={searchText}
            onChange={handleSearchChange}
            className="w-full p-2 border rounded"
            allowClear
            suffix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
          />
        </div> */}

        {/* Filters */}
        <div className="w-full p-5 rounded-lg bg-gray-100">
          <h4 className="font-inter text-lg font-medium mb-4">Filters</h4>
          <div className="flex flex-row flex-wrap gap-8">
            {/* Activity Dropdown */}
            <div className="flex-1 min-w-[250px]">
              <label className="block font-inter text-sm font-medium mb-1">Activity</label>
              <Select
                className="w-full"
                placeholder="Select Activity"
                loading={activitiesLoading}
                value={selectedActivity || undefined}
                onChange={(value) => {
                  setSelectedActivity(value);
                  setSelectedTask(null);
                  setSelectedSubtask(null);
                }}
              >
                <Select.Option value={null}>Select Activity</Select.Option>
                {activities.map((activity) => (
                  <Select.Option key={activity.id} value={activity.id}>
                    {activity.name} ({activity.code})
                  </Select.Option>
                ))}
              </Select>
            </div>

            {/* Task Dropdown */}
            <div className="flex-1 min-w-[250px]">
              <label className="block font-inter text-sm font-medium mb-1">Task</label>
              <Select
                className="w-full"
                placeholder="Select Task"
                loading={tasksLoading}
                value={selectedTask || undefined}
                onChange={(value) => {
                  setSelectedTask(value);
                  setSelectedSubtask(null);
                }}
                disabled={!selectedActivity}
              >
                <Select.Option value={null}>Select Task</Select.Option>
                {tasks.map((task) => (
                  <Select.Option key={task.id} value={task.id}>
                    {task.name} ({task.code})
                  </Select.Option>
                ))}
              </Select>
            </div>

            {/* Subtask Dropdown */}
            <div className="flex-1 min-w-[250px]">
              <label className="block font-inter text-sm font-medium mb-1">Subtask</label>
              <Select
                className="w-full"
                placeholder="Select Subtask"
                loading={subtasksLoading}
                value={selectedSubtask || undefined}
                onChange={(value) => setSelectedSubtask(value)}
                disabled={!selectedTask}
              >
                <Select.Option value={null}>Select Subtask</Select.Option>
                {subtasks.map((subtask) => (
                  <Select.Option key={subtask.id} value={subtask.id}>
                    {subtask.name} ({subtask.code})
                  </Select.Option>
                ))}
              </Select>
            </div>

            {/* Stars Dropdown */}
            <div className="flex-1 min-w-[200px]">
              <label className="block font-inter text-sm font-medium mb-1">Stars</label>
              <Select
                className="w-full"
                placeholder="Select Star"
                loading={expertsLoading}
                value={selectedStar || undefined}
                onChange={(value) => {
                  setSelectedStar(value);
                  if (value) {
                    setSelectedState(null);
                    setSelectedCity(null);
                  }
                }}
              >
                <Select.Option value={null}>Select Star</Select.Option>
                {experts.map((expert) => (
                  <Select.Option key={expert.id} value={expert.id}>
                    {expert.name}
                  </Select.Option>
                ))}
              </Select>
            </div>

            {/* Created At Dropdown */}
            <div className="flex-1 min-w-[200px]">
              <label className="block font-inter text-sm font-medium mb-1">Created At</label>
              <Select
                className="w-full"
                placeholder="Select Created At"
                value={selectedCreatedAt || undefined}
                onChange={(value) => setSelectedCreatedAt(value)}
              >
                <Select.Option value={null}>Select Created At</Select.Option>
                <Select.Option value="newest">Newest</Select.Option>
                <Select.Option value="oldest">Oldest</Select.Option>
              </Select>
            </div>

            {/* Language Dropdown */}
            <div className="flex-1 min-w-[200px]">
              <label className="block font-inter text-sm font-medium mb-1">Language</label>
              <Select
                className="w-full"
                placeholder="Select Language"
                loading={languagesLoading}
                value={selectedLanguage || undefined}
                onChange={(value) => setSelectedLanguage(value)}
              >
                <Select.Option value={null}>Select Language</Select.Option>
                {[...languages]
                  .sort((a, b) => {
                    const aPreferred = userData?.preferredLanguages?.some(
                      (prefLang) => prefLang.code === a.code || prefLang.id === a.id
                    );
                    const bPreferred = userData?.preferredLanguages?.some(
                      (prefLang) => prefLang.code === b.code || prefLang.id === b.id
                    );
                    if (aPreferred && !bPreferred) return -1;
                    if (!aPreferred && bPreferred) return 1;
                    return 0;
                  })
                  .map((language) => {
                    const isPreferred = userData?.preferredLanguages?.some(
                      (prefLang) => prefLang.code === language.code || prefLang.id === language.id
                    );
                    return (
                      <Select.Option key={language.id} value={language.id}>
                        <div className="flex justify-between items-center w-full">
                          <span>{language.name} ({language.code})</span>
                          {isPreferred && <span className="font-bold text-[black] ml-2">(User Preferred)</span>}
                        </div>
                      </Select.Option>
                    );
                  })}
              </Select>
            </div>

            {/* State Dropdown */}
            <div className="flex-1 min-w-[250px]">
              <label className="block font-inter text-sm font-medium mb-1">State</label>
              <Select
                className="w-full"
                placeholder="Select State"
                loading={statesLoading}
                value={selectedState || undefined}
                onChange={(value) => {
                  setSelectedState(value);
                  setSelectedCity(null);
                }}
                disabled={!!selectedStar}
              >
                <Select.Option value={null}>Select State</Select.Option>
                {states.map((state) => (
                  <Select.Option key={state.id} value={state.id}>
                    {state.stateName}
                  </Select.Option>
                ))}
              </Select>
            </div>

            {/* City Dropdown */}
            <div className="flex-1 min-w-[200px]">
              <label className="block font-inter text-sm font-medium mb-1">City</label>
              <Select
                className="w-full"
                placeholder="Select City"
                loading={citiesLoading}
                value={selectedCity || undefined}
                onChange={(value) => setSelectedCity(value)}
                disabled={!selectedState || !!selectedStar}
              >
                <Select.Option value={null}>Select City</Select.Option>
                {cities.map((city) => (
                  <Select.Option key={city.id} value={city.id}>
                    {city.cityName}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </div>
          <div className="mt-4 text-right">
            <Button
              className={`hover:text-white ${isFilterApplied() ? "bg-[#AD0101] hover:bg-[#AD0101]" : "bg-gray-400"} text-white`}
              onClick={handleApplyFilters}
              style={{ color: "white", backgroundColor: isFilterApplied() ? "#AD0101" : "gray" }}
              loading={applying}
              disabled={!isFilterApplied()}
            >
              Apply
            </Button>
          </div>
        </div>

        {/* Video Grid */}
        <div
          className="w-full p-2 rounded-lg relative"
          ref={videoGridRef}
          onScroll={(e) => {
            const bottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 100;
            if (bottom && hasNextPage && !loadingMore) {
              handleLoadMore();
            }
          }}
          style={{ maxHeight: "500px", overflowY: "auto", position: "relative" }}
        >
          <div className="grid grid-cols-4 gap-4">
            {videos.map((video, index) => (
              <div
                key={video.id}
                className={`relative cursor-pointer ${selectedVideos.includes(index) ? "border-4 border-[#AD0101]" : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleVideoClick(index);
                }}
              >
                {/* Thumbnail Image */}
                <div className="relative" style={{ paddingBottom: "56.25%" }}>
                  <img
                    src={video.vimeoThumbnailUrl}
                    alt={video.title}
                    className="absolute top-0 left-0 w-full h-full object-cover rounded-lg"
                  />
                </div>

                {/* Details Below Thumbnail */}
                <div className="mt-2">
                  {/* Title with Tooltip */}
                  <h3
                    ref={titleRefs.current[index]}
                    className="text-sm font-medium text-gray-900 truncate"
                    onMouseEnter={(e) => handleTitleHover(index, e)}
                    onMouseLeave={() => setActiveTooltipIndex(null)}
                    title={video.title.length > 60 ? video.title : ""}
                  >
                    {video.title.length > 60 ? `${video.title.substring(0, 60)}...` : video.title}
                  </h3>

                  {/* Badges */}
                  <div className="flex flex-wrap gap-2 mt-1">
                    <span
                      className="text-xs px-2 py-1 rounded-full"
                      style={{ backgroundColor: "#fee", color: "#d32f2f", display: "flex", alignItems: "center", gap: "6px" }}
                    >
                      <TranslateIcon style={{ width: "12px", height: "12px" }} />
                      {video.language}
                    </span>
                    <span
                      className="text-xs px-2 py-1 rounded-full"
                      style={{ backgroundColor: "#f0f0f0", color: "#555", display: "flex", alignItems: "center", gap: "6px" }}
                    >
                      <CategoryOutlinedIcon style={{ width: "12px", height: "12px" }} />
                      {video.fourmatCategory}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {loadingMore && (
            <div className="loading-container">
              <div className="spinner"></div>
            </div>
          )}
          {noMoreVideos && <p className="text-center mt-4">No more videos</p>}
          {/* Conditionally render Next Button only when step === 1 */}
          {step === 1 && (
            <div className="fixed bottom-4 right-12 z-10">
              <Button
                className={`hover:text-white ${selectedVideos.length > 0 ? "bg-[#AD0101] hover:bg-[#AD0101]" : "bg-gray-400"} text-white`}
                onClick={handleNext}
                style={{ color: "white", backgroundColor: selectedVideos.length > 0 ? "#AD0101" : "gray" }}
                disabled={selectedVideos.length === 0}
              >
                Next
              </Button>
            </div>
          )}
        </div>

        {/* Notification Details (shown when step === 2) */}
        {step === 2 && (
          <div ref={notificationSectionRef} className="mt-4 flex flex-col gap-4">
            {/* Selected Videos Preview */}
            <div className="mb-4">
              <h4 className="font-inter text-lg font-medium mb-2">Selected Videos</h4>
              <div className="grid grid-cols-4 gap-4">
                {selectedVideos.map((index) => {
                  const video = videos[index];
                  return (
                    <div
                      key={video.id}
                      className="relative"
                      style={{ paddingBottom: "56.25%" }}
                    >
                      <img
                        src={video.vimeoThumbnailUrl}
                        alt={video.title}
                        className="absolute top-0 left-0 w-full h-full object-cover rounded-lg"
                      />
                      <div className="absolute bottom-0 left-0 w-full bg-black bg-opacity-70 text-white p-2 text-sm truncate">
                        {video.title}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Notification Input Fields */}
            <div>
              <label className="block font-inter text-sm font-medium mb-1">Title</label>
              <Input
                placeholder="Enter notification title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full p-2 border rounded"
              />
            </div>
            <div>
              <label className="block font-inter text-sm font-medium mb-1">Description</label>
              <Input.TextArea
                placeholder="Enter notification description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full p-2 border rounded"
                rows={4}
              />
            </div>
            <div className="mt-4 text-right">
              <Button
                className={`hover:text-white ${title && description ? "bg-[#AD0101] hover:bg-[#AD0101]" : "bg-gray-400"} text-white`}
                onClick={handleSendVideos}
                style={{ color: "white", backgroundColor: title && description ? "#AD0101" : "gray" }}
                loading={sending}
                disabled={!title || !description}
              >
                Send
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default PushVideosFromAdminPanel;